import React, { useEffect, useState, useRef } from 'react';
import { apiFetch } from '../utils/api';
import { Helmet } from 'react-helmet-async';
import linkedinLogo from '../assets/LI-In-Bug.png';
import '../styles/Team.css';
import PositionConstants from '../constants/positions';
import '../styles/_colors.css';
import '../styles/_typography.css';
import '../styles/main.css';

// Removed BACKEND_URL, use apiFetch instead

export default function Team() {
  const [members, setMembers] = useState([]);
  const [projectGroups, setProjectGroups] = useState([]);
  const [teamSettings, setTeamSettings] = useState({
    teamImage: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=600&q=80',
    teamImageAlt: 'Our minimalist Scandinavian-style team workspace'
  });
  const [quantClubImage, setQuantClubImage] = useState('');
  const aboutRef = useRef(null);
  const valuesRef = useRef(null);
  const membersRef = useRef(null);

  useEffect(() => {
    // Fetch team members
    apiFetch('/api/members')
      .then(data => {
        setMembers(data.members || []);
      })
      .catch(() => setMembers([])); // Silently fail, do not show error

    // Fetch team settings
    apiFetch('/api/team-settings')
      .then(data => {
        setTeamSettings({
          teamImage: data.teamImage || 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=600&q=80',
          teamImageAlt: data.teamImageAlt || 'Our minimalist Scandinavian-style team workspace'
        });
      })
      .catch(() => {}); // Silently fail, use defaults

    // Fetch project groups
    apiFetch('/api/project-groups')
      .then(data => {
        setProjectGroups(Array.isArray(data) ? data : []);
      })
      .catch(() => setProjectGroups([]));

    // Fetch Quant Club image
    apiFetch('/api/quant-club')
      .then(data => {
        const club = Array.isArray(data) && data.length > 0 ? data[0] : null;
        setQuantClubImage(club && club.image ? club.image : '');
      })
      .catch(() => setQuantClubImage(''));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const refs = [aboutRef, valuesRef, membersRef];
    refs.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <div className="team-container">
      <Helmet>
        <title>NHHS Analytics | Our Team</title>
      </Helmet>
      {/* Hero section */}
      <section className="team-hero">
        <div className="team-hero-content">
          <h1>Meet the Team</h1>
          <p>
            We're a new student group at NHH with big ambitions and fresh ideas. 
            Our team is small but passionate: students from different backgrounds and years at NHH, 
            all united by our curiosity about data and analytics.
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">{members.length}</div>
              <div className="stat-label">Team Members</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">2024</div>
              <div className="stat-label">Founded</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">∞</div>
              <div className="stat-label">Potential</div>
            </div>
          </div>
        </div>
      </section>

      {/* About section */}
      <section className="team-about-section" ref={aboutRef}>
        <div className="team-about-container">
          <div className="team-about-content">
            <h2>Building from the Ground Up</h2>
            <div className="team-about-text">
              <p>
                Starting from scratch means we get to build something exactly how we envision it—no old habits, 
                just pure curiosity about what's possible with data. We're figuring it out as we go, 
                and that's exactly what makes it exciting.
              </p>
              <p>
                Want to be part of something from the ground up? We're looking for early members who want to 
                help shape what NHHS Analytics becomes. No experience required—just bring your curiosity.
              </p>
              <div className="team-about-buttons">
                <a href="/join" className="team-btn primary">
                  Join Our Team
                </a>
                <a href="/projects" className="team-btn secondary">
                  See Our Work
                </a>
              </div>
            </div>
          </div>
          <div className="team-about-image">
            <img
              src={teamSettings.teamImage}
              alt={teamSettings.teamImageAlt}
            />
          </div>
        </div>
      </section>

      {/* NHHS Analytics Subgroups Section */}
      <section className="team-subgroups-section" ref={valuesRef}>
        <div className="team-subgroups-container">
          <div className="team-subgroups-header">
            <h2>Subgroups of NHHS Analytics</h2>
            <p>We foster multiple initiatives that allow students to explore different areas of data, business, and finance under the NHHS Analytics umbrella.</p>
          </div>
          
          <div className="team-subgroups-grid">
            <div className="subgroup-card subgroup-flex">
              <div className="subgroup-content">
                <h3>Quant Club</h3>
                <p>
                  Quant Club, a specialized subgroup within NHHS Analytics, focuses on quantitative analysis and financial modeling. 
                </p>
                <a
                  href="/quant-club"
                  className="read-more-link"
                  style={{ display: 'inline-block', marginTop: '8px', color: '#1976d2', textDecoration: 'underline', fontWeight: 500 }}
                >
                  Read more
                </a>
              </div>
              {quantClubImage && (
                <div className="subgroup-image-wrapper">
                  <img
                    src={quantClubImage}
                    alt="Quant Club"
                    style={{maxWidth:180,borderRadius:12,boxShadow:'0 2px 8px rgba(0,0,0,0.08)'}}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* NHHS Analytics Project Groups Section */}
      <section className="team-projectgroups-section" ref={valuesRef}>
        <div className="team-projectgroups-container">
          <div className="team-projectgroups-header">
            <h2>Explore Project Groups</h2>
            <p>Join a project group to dive into analytics, business, and finance with fellow students. Each group offers unique opportunities to learn, build, and grow.</p>
          </div>
          
          <div className="team-projectgroups-grid">
            {projectGroups.length === 0 && (
              <div style={{color:'#888',padding:'1rem'}}>No project groups yet.</div>
            )}
            {projectGroups.map(group => (
              <div className="projectgroup-card" key={group._id || group.name}>
                <h3>{group.name}</h3>
                <p>{group.description || 'No description provided.'}</p>
                <a
                  href={`/project-groups/${encodeURIComponent(group.name)}`}
                  className="read-more-link"
                  style={{ display: 'inline-block', marginTop: '8px', color: '#1976d2', textDecoration: 'underline', fontWeight: 500 }}
                >
                  Read more
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team members section */}
      <section className="team-members-section" ref={membersRef}>
        <div className="team-members-container">
          <h2>Our Team</h2>
          <p className="team-section-subtitle">
            Meet the passionate individuals driving innovation in analytics at NHH
          </p>
          {/* Sort members by position priority, then name */}
          <div className="team-members-grid">
            {[...members].sort((a, b) => {
              // Get positions array for each member
              const getSortedPositions = m => {
                let arr = Array.isArray(m.positions) ? m.positions : Array.isArray(m.position) ? m.position : m.position ? [m.position] : [];
                arr = arr.filter(Boolean);
                return PositionConstants.OPTIONS.filter(opt => arr.includes(opt));
              };
              const aPos = getSortedPositions(a);
              const bPos = getSortedPositions(b);
              // Compare by first position index in constants
              const aIdx = aPos.length > 0 ? PositionConstants.OPTIONS.indexOf(aPos[0]) : PositionConstants.OPTIONS.length;
              const bIdx = bPos.length > 0 ? PositionConstants.OPTIONS.indexOf(bPos[0]) : PositionConstants.OPTIONS.length;
              if (aIdx !== bIdx) return aIdx - bIdx;
              // If same position, sort by name
              return a.name.localeCompare(b.name);
            }).map(member => {
              return (
                <div className="team-member-card" key={member._id || member.name}>
                  <div className="team-member-photo-wrapper">
                    <img
                      className="team-member-photo"
                      src={member.image || '/default-profile.svg'}
                      alt={member.name}
                      loading="lazy"
                    />
                  </div>
                  <div className="team-member-info">
                    <h4 className="team-member-name">{member.name}</h4>
                    {/* Support both 'position' and 'positions' for backward compatibility */}
                    {(() => {
                      // Get positions array from either field
                      let positionsArr = Array.isArray(member.positions) ? member.positions : Array.isArray(member.position) ? member.position : member.position ? [member.position] : [];
                      // Sort by PositionConstants.OPTIONS order
                      positionsArr = positionsArr.filter(Boolean);
                      const sorted = PositionConstants.OPTIONS.filter(opt => positionsArr.includes(opt));
                      return (
                        <>
                          <span className="team-member-role">{sorted.length > 0 ? sorted.join(', ') : 'Member'}</span>
                          {member.linkedin && (
                            <div className="team-member-linkedin-wrapper">
                              <a
                                href={member.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="team-member-linkedin"
                                aria-label={`${member.name}'s LinkedIn profile`}
                              >
                                <img
                                  src={linkedinLogo}
                                  alt="LinkedIn"
                                  className="team-member-linkedin-logo"
                                />
                              </a>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
